import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { API_BASE_URL, authFetch } from "../../config/api";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getSettlementAmount = (value) => Math.abs(Number(value || 0));

const getSettlementLabel = (value) => {
  const numeric = Number(value || 0);
  if (numeric < 0) return "Return";
  if (numeric > 0) return "Reimbursement";
  return "Liquidation";
};

const getDocumentKind = (doc = {}) => {
  const fileName = String(doc.file_name || doc.file_path || "").toLowerCase();
  if (fileName.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName)) return "image";
  return "other";
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const LiquidationTeamLeaderApprovals = () => {
  const user = useSelector((state) => state.auth.user);
  const userId = Number(user?.user_id || user?.id || 0);
  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState("");
  const [verifyModalRow, setVerifyModalRow] = useState(null);
  const verifyModalLiquidation = verifyModalRow?.liquidation || {};
  const verifyModalReport = verifyModalRow?.liquidation_report || null;
  const verifyModalDocuments = verifyModalRow?.liquidation_documents || [];

  const loadRows = async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/liquidations`);
      url.searchParams.set("stage", "submitted");
      const res = await authFetch(url, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) {
        throw new Error(json.message || "Failed to load liquidation approvals");
      }
      setRows(json.data || []);
    } catch (err) {
      setError(err.message || "Failed to load liquidation approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const pendingRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (rows || [])
      .filter(
        (row) =>
          row?.liquidation &&
          String(row.liquidation.team_leader_user_id || "") !== "" &&
          String(row.liquidation_stage || "").toLowerCase() === "submitted"
      )
      .filter((row) => {
        if (!term) return true;
        const liquidation = row.liquidation || {};
        return [
          row.rfp_no,
          row.payee,
          row.job_order,
          liquidation.team_leader_name,
          liquidation.status,
          row.liquidation_summary,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => {
        const aDate = new Date(a.liquidation?.updated_at || a.updated_at || 0).getTime();
        const bDate = new Date(b.liquidation?.updated_at || b.updated_at || 0).getTime();
        return bDate - aDate;
      });
  }, [rows, search]);

  const approveRow = async (row) => {
    if (!row?.id) return;
    const liquidation = row.liquidation || {};
    const assignedLeaderId = Number(liquidation.team_leader_user_id || 0);
    if (!isSuperadmin && assignedLeaderId !== userId) {
      return;
    }

    setSavingId(String(row.id));
    setError("");
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/liquidations/${row.id}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "verify",
          }),
        }
      );
      const json = await readJsonResponse(res);
      if (!json.success) {
        throw new Error(json.message || "Failed to approve liquidation");
      }

      const updatedLiquidation = json.data?.liquidation || {};
      setRows((current) =>
        current.map((item) =>
          String(item.id) === String(row.id)
            ? {
                ...item,
                liquidation: updatedLiquidation,
                liquidation_stage: json.data?.liquidation_stage || "approved",
              }
            : item
        )
      );
    } catch (err) {
      setError(err.message || "Failed to approve liquidation");
    } finally {
      setSavingId("");
      setVerifyModalRow(null);
    }
  };

  return (
    <div className="min-h-full bg-slate-50 p-6 lg:p-8 font-poppins">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-[2rem] border border-white/60 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Liquidation
              </p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">
                Team Leader Liquidation Approval
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                This page lists liquidations that are waiting for the assigned team leader to
                approve the liquidation after reviewing the hardcopy and report.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Pending</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">{pendingRows.length}</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Assigned</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">
                  {pendingRows.filter((row) => Number(row.liquidation?.team_leader_user_id || 0) === userId).length}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Accessible</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">Yes</div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search RFP, payee, job order, or team leader..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm outline-none ring-0 transition focus:border-blue-400"
              />
            </div>
            <button
              type="button"
              onClick={loadRows}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <i className="fa-solid fa-rotate-right" />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-[2rem] border border-white/60 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Pending team leader approvals</h2>
              <p className="text-sm text-slate-600">
                Approve liquidations assigned to a team leader. Approved items disappear from this list.
              </p>
            </div>
            <div className="text-sm text-slate-500">
              {pendingRows.length} item{pendingRows.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500">Loading pending approvals...</div>
          ) : pendingRows.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-6 py-4">RFP No</th>
                    <th className="px-6 py-4">Payee</th>
                    <th className="px-6 py-4">Job Order</th>
                    <th className="px-6 py-4">Team Leader</th>
                    <th className="px-6 py-4 text-right">Status</th>
                    <th className="px-6 py-4">Submitted</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {pendingRows.map((row) => {
                    const liquidation = row.liquidation || {};
                    const assignedLeaderId = Number(liquidation.team_leader_user_id || 0);
                    const canApprove = isSuperadmin || assignedLeaderId === userId;
                    return (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-semibold text-blue-700">{row.rfp_no || "—"}</td>
                        <td className="px-6 py-4 text-slate-900">{row.payee || "—"}</td>
                        <td className="px-6 py-4 text-slate-600">{row.job_order || "—"}</td>
                        <td className="px-6 py-4 text-slate-700">
                          <div className="font-medium">{liquidation.team_leader_name || "—"}</div>
                          <div className="text-xs text-slate-500">
                            {assignedLeaderId ? `User ID ${assignedLeaderId}` : "No team leader selected"}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-semibold text-slate-900">
                          <span className="inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                            {String(liquidation.status || "submitted").replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {formatDateTime(liquidation.submitted_at || liquidation.updated_at || row.updated_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {canApprove ? (
                            <button
                              type="button"
                              onClick={() => setVerifyModalRow(row)}
                              disabled={savingId === String(row.id)}
                              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {savingId === String(row.id) ? (
                                <>
                                  <i className="fa-solid fa-spinner fa-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-circle-check" />
                                  Approve Liquidation
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="inline-flex rounded-full bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                              Awaiting assigned team leader
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No liquidations are waiting for team leader approval.
            </div>
          )}
        </div>
      </div>

      {verifyModalRow
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/55 px-4">
              <div
                className="absolute inset-0"
                role="presentation"
                onClick={() => !savingId && setVerifyModalRow(null)}
              />
              <div className="relative z-10 flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                <div className="shrink-0 border-b border-slate-100 p-6 lg:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Confirm approval
                      </p>
                      <h3 className="mt-2 text-2xl font-bold text-slate-900">
                        Approve this liquidation?
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => !savingId && setVerifyModalRow(null)}
                      className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Close verification modal"
                      disabled={savingId === String(verifyModalRow.id)}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-700">
                    <div className="font-semibold text-slate-900">
                      {verifyModalRow.rfp_no || "No RFP number"}
                    </div>
                    <div className="mt-1">
                      {verifyModalRow.payee || "—"} | {verifyModalRow.job_order || "No job order"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      This will approve the liquidation and move it to the approved stage.
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                  <div className="grid gap-6 xl:grid-cols-2">
                    <div className="space-y-6">
                      <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Liquidation report
                          </h4>
                          <span className="text-xs text-slate-500">
                            {verifyModalReport ? "Available" : "Not yet saved"}
                          </span>
                        </div>

                        {verifyModalReport ? (
                          <div className="mt-3 space-y-4">
                            <div className="rounded-2xl border border-white bg-white p-4 shadow-sm">
                              <div className="grid gap-3 text-sm sm:grid-cols-2">
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Report date</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.report_date_created
                                      ? new Date(verifyModalReport.report_date_created).toLocaleDateString()
                                      : formatDateTime(verifyModalReport.created_at || verifyModalRow.created_at)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">JO</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.job_order || verifyModalRow.job_order || "—"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Tracking no</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.tracking_no || "—"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Reference no</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.reference_no || "—"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">P.O no</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.po_no || "—"}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Delivery date</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.delivery_date || "—"}
                                  </div>
                                </div>
                                <div className="sm:col-span-2">
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-500">Shipment details</div>
                                  <div className="mt-1 font-semibold text-slate-900">
                                    {verifyModalReport.shipment_details || verifyModalLiquidation.shipment_details || "—"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="overflow-hidden rounded-2xl border border-white bg-white shadow-sm">
                              <div className="bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white">
                                Cost incurred for shipment
                              </div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.08em] text-slate-500">
                                    <tr>
                                      <th className="px-4 py-3">Particulars</th>
                                      <th className="px-4 py-3">OR No.</th>
                                      <th className="px-4 py-3 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {(verifyModalReport.actual_cost_rows || []).length ? (
                                      verifyModalReport.actual_cost_rows.map((entry) => (
                                        <tr key={entry.id || `${entry.particulars}-${entry.or_no}`}>
                                          <td className="px-4 py-3 text-slate-700">
                                            <div className="font-medium">{entry.particulars || "—"}</div>
                                            {entry.is_unliquidated ? (
                                              <div className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-amber-700 ring-1 ring-amber-200">
                                                Unliquidated
                                              </div>
                                            ) : null}
                                          </td>
                                          <td className="px-4 py-3 text-slate-700">{entry.or_no || "—"}</td>
                                          <td className="px-4 py-3 text-right font-medium text-slate-900">
                                            ₱{formatMoney(entry.amount)}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td className="px-4 py-4 text-center text-slate-500" colSpan={3}>
                                          No cost rows available.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <div className="border-t border-slate-100 px-4 py-3 text-right text-sm font-semibold text-slate-900">
                                TOTAL ACTUAL COST INCURRED: ₱{formatMoney(verifyModalReport.total_actual_cost_incurred)}
                              </div>
                            </div>

                            <div className="overflow-hidden rounded-2xl border border-white bg-white shadow-sm">
                              <div className="bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white">
                                Advances table
                              </div>
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.08em] text-slate-500">
                                    <tr>
                                      <th className="px-4 py-3">Particulars</th>
                                      <th className="px-4 py-3">OR No.</th>
                                      <th className="px-4 py-3 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {(verifyModalReport.advance_rows || []).length ? (
                                      verifyModalReport.advance_rows.map((entry) => (
                                        <tr key={entry.id || `${entry.particulars}-${entry.or_no}`}>
                                          <td className="px-4 py-3 text-slate-700">{entry.particulars || "—"}</td>
                                          <td className="px-4 py-3 text-slate-700">{entry.or_no || "—"}</td>
                                          <td className="px-4 py-3 text-right font-medium text-slate-900">
                                            ₱{formatMoney(entry.amount)}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td className="px-4 py-4 text-center text-slate-500" colSpan={3}>
                                          No advance rows available.
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                              <div className="border-t border-slate-100 px-4 py-3 text-right text-sm font-semibold text-slate-900">
                                TOTAL ADVANCES: ₱{formatMoney(verifyModalReport.total_advances)}
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700">
                              <div>{getSettlementLabel(verifyModalReport.total_reimbursement_return)}:</div>
                              <div>₱{formatMoney(getSettlementAmount(verifyModalReport.total_reimbursement_return))}</div>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                            The liquidation report is not available yet.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Preview
                        </h4>
                        <span className="text-xs text-slate-500">
                          {verifyModalDocuments.length} file{verifyModalDocuments.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="mt-3 space-y-3 pr-1">
                        {verifyModalDocuments.length ? (
                          verifyModalDocuments.map((doc) => {
                            const kind = getDocumentKind(doc);
                            return (
                              <div key={doc.id || doc.file_path} className="rounded-2xl border border-white bg-white p-3 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="truncate font-semibold text-slate-900">
                                      {doc.file_name || "Unnamed file"}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {doc.doc_type || "document"} | {formatDateTime(doc.uploaded_at)}
                                    </div>
                                  </div>
                                  {doc.file_url ? (
                                    <a
                                      href={doc.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                                    >
                                      Open
                                    </a>
                                  ) : null}
                                </div>

                                <div className="mt-3 overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
                                  {kind === "image" && doc.file_url ? (
                                    <img
                                      src={doc.file_url}
                                      alt={doc.file_name || "Liquidation document"}
                                      className="h-56 w-full object-contain bg-white"
                                    />
                                  ) : kind === "pdf" && doc.file_url ? (
                                    <iframe
                                      title={doc.file_name || "Liquidation document"}
                                      src={doc.file_url}
                                      className="h-56 w-full"
                                    />
                                  ) : (
                                    <div className="flex h-56 items-center justify-center px-4 text-center text-sm text-slate-500">
                                      Preview not available. Open the file to view it.
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                            No liquidation documents uploaded yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shrink-0 border-t border-slate-100 bg-white p-6 lg:p-8">
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => !savingId && setVerifyModalRow(null)}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={savingId === String(verifyModalRow.id)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => approveRow(verifyModalRow)}
                    disabled={savingId === String(verifyModalRow.id)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingId === String(verifyModalRow.id) ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-circle-check" />
                        Verify Hardcopy
                      </>
                    )}
                  </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default LiquidationTeamLeaderApprovals;
